/**
 * Indian Entity Verify — LLP, MSME/Udyam, Partnership/Sole-Prop explainer.
 * Surfaces what MCA21 cannot (LLP + MSME) plus an honest "no central registry"
 * card for Partnership / Sole Proprietorship with the right verification path.
 */
import React, { useState } from "react";
import axios from "axios";
import {
  Building2, Sparkles, Loader2, Briefcase, Factory, ShieldAlert,
  CheckCircle2, AlertTriangle, Users, MapPin, Calendar, Hash,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api/indian-entity";

const TABS = [
  { id: "llp",         label: "LLP",                Icon: Briefcase },
  { id: "msme",        label: "MSME / Udyam",       Icon: Factory },
  { id: "partnership", label: "Partnership",        Icon: Users },
  { id: "soleprop",    label: "Sole Proprietorship", Icon: ShieldAlert },
];

export default function IndianEntityPage() {
  const [tab, setTab] = useState("llp");

  return (
    <div data-testid="indian-entity-page" style={{ maxWidth: 1120, margin: "0 auto" }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", margin: 0,
                     display: "inline-flex", alignItems: "center", gap: 10 }}>
        <Building2 size={22} color="#2563eb" /> India Entity Verify
      </h1>
      <p style={{ color: "#64748b", fontSize: 13, marginTop: 6, marginBottom: 20 }}>
        LLP (MCA LLP portal), MSME/Udyam (Ministry of MSME), and an honest
        verification roadmap for entities without a central registry.
      </p>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap" style={{ marginBottom: 18 }}>
        {TABS.map(t => {
          const active = tab === t.id;
          const I = t.Icon;
          return (
            <button key={t.id} data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              style={{
                padding: "8px 16px", borderRadius: 10,
                background: active ? "#1e3a5f" : "#0d1117",
                border: `1px solid ${active ? "#2563eb" : "#1e2530"}`,
                color: active ? "#f1f5f9" : "#94a3b8",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>
              <I size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "llp" && <LLPTab />}
      {tab === "msme" && <UdyamTab />}
      {tab === "partnership" && <ExplainerTab type="partnership" />}
      {tab === "soleprop" && <ExplainerTab type="sole_proprietorship" />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LLP
// ---------------------------------------------------------------------------

function LLPTab() {
  const [llpin, setLlpin] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const onLookup = async () => {
    if (!llpin.trim()) return toast.error("Enter an LLPIN");
    setLoading(true); setData(null);
    try {
      const res = await axios.post(`${API}/llp`, { llpin },
                                     { withCredentials: true });
      setData(res.data);
      toast.success(res.data._mode === "live" ? "Live LLP record fetched" : "Demo data loaded");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Lookup failed");
    } finally { setLoading(false); }
  };

  return (
    <>
      <SearchPanel
        testIdPrefix="llp"
        id={llpin} setId={setLlpin} placeholder="LLPIN (e.g. AAA-1234)"
        loading={loading} onSubmit={onLookup}
      />
      {data && (
        <>
          <ModeBadge mode={data._mode} note={data._source} />
          <Card title={data.name} subtitle={`LLPIN ${data.llpin}`}
                badge={data.status} badgeColor={data.status?.toLowerCase().includes("active") ? "#10b981" : "#f59e0b"}>
            <KV pairs={[
              ["Incorporation date", data.incorporation_date],
              ["ROC", data.roc],
              ["Registered address", data.registered_address],
              ["Obligation of contribution", data.obligation_of_contribution],
              ["Total partners", data.total_partners],
              ["Designated partners", data.designated_partners],
              ["Last Form 8 filed", data.last_form_8_filed],
              ["Last Form 11 filed", data.last_form_11_filed],
              ["Filing status", data.filing_status],
            ]} />
          </Card>
          {(data.partners || []).length > 0 && (
            <Card title={`Partners (${data.partners.length})`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.partners.map((p, i) => (
                  <div key={i} data-testid={`llp-partner-${i}`} style={partnerBoxStyle}>
                    <div style={{ color: "#f1f5f9", fontWeight: 700, fontSize: 13 }}>{p.name}</div>
                    <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2 }}>
                      DIN/DPIN: <code>{p.din}</code>
                    </div>
                    <div style={{ color: "#64748b", fontSize: 11 }}>
                      {p.designation} · appointed {p.date_of_appointment}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Udyam (MSME) — LIVE via Surepass /udyam-otp/send-otp + /submit-otp
// ---------------------------------------------------------------------------

const SUREPASS_API = `${window.location.origin}/api/v1/surepass`;
// Udyam certificates look like: UDYAM-<STATE 2 letters>-<DIST 2 digits>-<7 digits>
const UDYAM_RE = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;

function UdyamTab() {
  const [udyam, setUdyam] = useState("");
  const [mobile, setMobile] = useState("");
  const [clientId, setClientId] = useState(null);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("input");   // input | otp | result
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [udyamErr, setUdyamErr] = useState(null);
  const [mobileErr, setMobileErr] = useState(null);

  const validateUdyam = (v) => {
    const s = (v || "").trim().toUpperCase();
    if (!s) return "Udyam number is required";
    if (!UDYAM_RE.test(s)) {
      return "Invalid format. Expected UDYAM-XX-00-0000000 (state 2 letters, district 2 digits, then 7 digits).";
    }
    return null;
  };
  const validateMobile = (v) => {
    const m = (v || "").replace(/\D/g, "");
    if (m.length !== 10) return "Mobile number must be exactly 10 digits";
    if (!/^[6-9]/.test(m)) return "Indian mobile numbers must start with 6, 7, 8 or 9";
    return null;
  };

  const sendOtp = async () => {
    const num = udyam.trim().toUpperCase();
    const mob = mobile.replace(/\D/g, "");
    const uErr = validateUdyam(num);
    const mErr = validateMobile(mob);
    setUdyamErr(uErr);
    setMobileErr(mErr);
    if (uErr || mErr) {
      toast.error(uErr || mErr);
      return;
    }
    setLoading(true); setData(null);
    try {
      const res = await axios.post(`${SUREPASS_API}/udyam-otp/send-otp`,
        { udyam_number: num, mobile_number: mob },
        { withCredentials: true, timeout: 25000 });
      const cid = res.data?.client_id;
      if (!cid || cid === "Not available on record") {
        throw new Error(res.data?.message || "Surepass did not return a client_id");
      }
      setClientId(cid);
      setStep("otp");
      toast.success("OTP sent to the mobile registered on the Udyam certificate");
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = (raw && typeof raw === "object" ? raw.message : raw)
                 || e?.message || "Failed to send OTP";
      // Surface Surepass-side errors inline on the Udyam input so the user
      // can see the reason (e.g. "Invalid Mobile Number or Registration Number").
      setUdyamErr(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const submitOtp = async () => {
    if (!otp || otp.length < 4) return toast.error("Enter the OTP");
    if (!clientId) { setStep("input"); return toast.error("Session expired — resend OTP"); }
    setLoading(true);
    try {
      const res = await axios.post(`${SUREPASS_API}/udyam-otp/submit-otp`,
        { client_id: clientId, otp }, { withCredentials: true, timeout: 30000 });
      setData(res.data);
      setStep("result");
      toast.success(`Verified — ${res.data.enterprise_name || "Udyam holder"}`);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = (raw && typeof raw === "object" ? raw.message : raw)
                 || e?.message || "OTP verification failed";
      toast.error(msg);
      // Wrong OTP → stay on this step and let the user retry.
      setOtp("");
    } finally { setLoading(false); }
  };

  // Normalize any separator ('=', '_', '/', '\\', spaces) into '-' so users
  // who use the wrong key still get a valid Udyam string.
  const normalizeUdyam = (v) =>
    (v || "").toUpperCase()
             .replace(/[=_/\\.\s]+/g, "-")
             .replace(/-+/g, "-")
             .slice(0, 22);

  const reset = () => {
    setUdyam(""); setMobile(""); setOtp(""); setClientId(null); setData(null); setStep("input");
  };

  const tierColor = data?.enterprise_type === "Micro" ? "#10b981"
                  : data?.enterprise_type === "Small" ? "#3b82f6"
                  : data?.enterprise_type === "Medium" ? "#f59e0b" : "#94a3b8";

  return (
    <>
      {step === "input" && (
        <div style={{
          background: "#0d1117", border: "1px solid #1e2530",
          borderRadius: 14, padding: 20, marginBottom: 16,
        }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: 0.6,
                          marginBottom: 6 }}>Udyam Registration Number</div>
          <input data-testid="udyam-input"
            value={udyam}
            onChange={e => {
              const v = normalizeUdyam(e.target.value);
              setUdyam(v);
              setUdyamErr(v ? validateUdyam(v) : null);
            }}
            onKeyDown={e => e.key === "Enter" && sendOtp()}
            placeholder="UDYAM-XX-00-0000000"
            style={{
              width: "100%", padding: "11px 14px", borderRadius: 10,
              background: "#080c12",
              border: `1px solid ${udyamErr ? "#ef4444" : "#1e2530"}`,
              color: "#f1f5f9", fontSize: 14,
              fontFamily: "ui-monospace, monospace",
              marginBottom: udyamErr ? 4 : 12,
            }} />
          {udyamErr && (
            <div data-testid="udyam-input-error"
                  style={{ color: "#ef4444", fontSize: 11, marginBottom: 10,
                             display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={11} /> {udyamErr}
            </div>
          )}
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: 0.6,
                          marginBottom: 6 }}>
            Mobile Number (10 digits, linked to Udyam certificate)
          </div>
          <input data-testid="udyam-mobile-input"
            value={mobile}
            onChange={e => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 10);
              setMobile(v);
              setMobileErr(v ? validateMobile(v) : null);
            }}
            onKeyDown={e => e.key === "Enter" && sendOtp()}
            placeholder="98XXXXXXXX"
            inputMode="numeric"
            maxLength={10}
            style={{
              width: "100%", padding: "11px 14px", borderRadius: 10,
              background: "#080c12",
              border: `1px solid ${mobileErr ? "#ef4444" : "#1e2530"}`,
              color: "#f1f5f9", fontSize: 14, letterSpacing: 3,
              fontFamily: "ui-monospace, monospace",
            }} />
          {mobileErr && (
            <div data-testid="udyam-mobile-error"
                  style={{ color: "#ef4444", fontSize: 11, marginTop: 4,
                             display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={11} /> {mobileErr}
            </div>
          )}
          <button onClick={sendOtp} disabled={loading}
                  data-testid="udyam-send-otp-btn"
                  style={{
                    marginTop: 12, padding: "10px 22px", borderRadius: 10,
                    background: loading ? "#1e2530" : "#2563eb",
                    color: "#fff", border: "none", fontSize: 13, fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                    display: "inline-flex", alignItems: "center", gap: 8,
                  }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {loading ? "Sending OTP…" : "Send OTP"}
          </button>
        </div>
      )}

      {step === "otp" && (
        <div data-testid="udyam-otp-panel" style={{
          background: "#0d1117", border: "1px solid #1e2530",
          borderRadius: 14, padding: 22, marginBottom: 14,
        }}>
          <div style={{ color: "#10b981", fontSize: 11, fontWeight: 700,
                          marginBottom: 8, display: "inline-flex",
                          alignItems: "center", gap: 6 }}>
            <CheckCircle2 size={12} /> OTP sent to the mobile registered on {udyam}
          </div>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: 0.6,
                          marginBottom: 8, marginTop: 10 }}>
            Enter OTP
          </div>
          <input
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="6-digit OTP"
            data-testid="udyam-otp-input"
            inputMode="numeric"
            maxLength={8}
            autoFocus
            style={{
              width: "100%", background: "#080c12", border: "1px solid #1e2530",
              borderRadius: 6, padding: "12px 14px", color: "#f1f5f9",
              fontSize: 18, letterSpacing: 8, textAlign: "center",
              fontFamily: "ui-monospace, monospace", marginBottom: 14,
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setStep("input")} data-testid="udyam-back-btn"
                    style={{ background: "transparent", border: "1px solid #1e2530",
                              color: "#94a3b8", borderRadius: 6, padding: "10px 14px",
                              fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              ← Re-enter Udyam
            </button>
            <button onClick={submitOtp} disabled={loading || otp.length < 4}
                    data-testid="udyam-verify-btn"
                    style={{
                      flex: 1, background: loading || otp.length < 4
                                ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
                      border: "none", color: "white", borderRadius: 6,
                      padding: "10px 18px", fontSize: 12, fontWeight: 700,
                      cursor: loading || otp.length < 4 ? "not-allowed" : "pointer",
                      display: "inline-flex", alignItems: "center",
                      justifyContent: "center", gap: 8,
                    }}>
              {loading ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={13} />}
              {loading ? "Verifying…" : "Verify OTP"}
            </button>
          </div>
        </div>
      )}

      {step === "result" && data && (
        <>
          <ModeBadge mode="live" note={data._source} />
          <Card title={data.enterprise_name}
                subtitle={`Udyam ${data.udyam_number}`}
                badge={data.enterprise_type}
                badgeColor={tierColor}>
            <KV pairs={[
              ["Major activity", data.major_activity],
              ["NIC code", data.nic_code],
              ["Investment in plant & machinery", data.investment_in_plant_machinery],
              ["Annual turnover", data.annual_turnover],
              ["Employees", String(data.employees)],
              ["Registration date", data.registration_date],
              ["State / District", `${data.state} · ${data.district}`],
              ["Pincode", data.pincode],
              ["Social category", data.social_category],
              ["Mobile", data.mobile],
              ["Email", data.email],
              ["Exporter", data.is_exporter ? "Yes" : "No"],
            ]} />
          </Card>
          <button onClick={reset} data-testid="udyam-new-btn"
                  style={{ background: "transparent", border: "1px solid #1e2530",
                             color: "#94a3b8", borderRadius: 6, padding: "8px 14px",
                             fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            New Verification
          </button>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// No-registry explainer  (Partnership / Sole Proprietorship)
// ---------------------------------------------------------------------------

function ExplainerTab({ type }) {
  const [info, setInfo] = useState(null);
  React.useEffect(() => {
    axios.get(`${API}/explain/${type}`, { withCredentials: true })
         .then(r => setInfo(r.data))
         .catch(() => {});
  }, [type]);
  if (!info) return <div style={{ color: "#64748b" }}>Loading…</div>;
  return (
    <div data-testid={`explainer-${type}`} style={{
      background: "#0d1117", border: "1px solid #f59e0b44",
      borderRadius: 14, padding: 24,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <AlertTriangle size={18} color="#f59e0b" />
        <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, margin: 0 }}>
          {info.title}
        </h3>
      </div>
      <p style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.7, marginTop: 0 }}>
        {info.explanation}
      </p>
      <div style={{ marginTop: 16 }}>
        <div style={{
          color: "#64748b", fontSize: 11, fontWeight: 700,
          letterSpacing: 0.7, textTransform: "uppercase", marginBottom: 8,
        }}>Recommended verification path</div>
        <ol style={{ color: "#f1f5f9", fontSize: 13, lineHeight: 1.8,
                       paddingLeft: 22, margin: 0 }}>
          {info.verification_steps.map((s, i) => (
            <li key={i} data-testid={`step-${type}-${i}`}>
              <CheckCircle2 size={12} color="#10b981" style={{ verticalAlign: -1, marginRight: 4 }} />
              {s}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function SearchPanel({ id, setId, placeholder, loading, onSubmit, testIdPrefix, buttonLabel }) {
  return (
    <div style={{
      background: "#0d1117", border: "1px solid #1e2530",
      borderRadius: 14, padding: 20, marginBottom: 16,
    }}>
      <input data-testid={`${testIdPrefix}-input`}
        value={id} onChange={e => setId(e.target.value.toUpperCase())}
        onKeyDown={e => e.key === "Enter" && onSubmit()}
        placeholder={placeholder}
        style={{
          width: "100%", padding: "11px 14px", borderRadius: 10,
          background: "#080c12", border: "1px solid #1e2530",
          color: "#f1f5f9", fontSize: 14,
          fontFamily: "ui-monospace, monospace",
        }} />
      <button onClick={onSubmit} disabled={loading}
              data-testid={`${testIdPrefix}-lookup-btn`}
        style={{
          marginTop: 12, padding: "10px 22px", borderRadius: 10,
          background: "#2563eb", color: "#fff", border: "none",
          fontSize: 13, fontWeight: 700, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 8,
        }}>
        {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
        {buttonLabel || "Lookup"}
      </button>
    </div>
  );
}

function ModeBadge({ mode, note }) {
  const live = mode === "live";
  return (
    <div data-testid="mode-badge" style={{
      marginBottom: 12, display: "flex", alignItems: "center", gap: 10,
    }}>
      <span style={{
        padding: "3px 10px", borderRadius: 999,
        background: live ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.12)",
        border: `1px solid ${live ? "#10b98144" : "#f59e0b44"}`,
        color: live ? "#10b981" : "#f59e0b",
        fontSize: 10, fontWeight: 800, letterSpacing: 0.7,
      }}>{live ? "LIVE" : "SAMPLE / MOCK"}</span>
      <span style={{ color: "#64748b", fontSize: 11 }}>{note}</span>
    </div>
  );
}

function Card({ title, subtitle, badge, badgeColor, children }) {
  return (
    <div style={{
      background: "#0d1117", border: "1px solid #1e2530",
      borderRadius: 14, padding: 22, marginBottom: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center",
                     justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 800 }}>{title}</div>
          {subtitle && <div style={{ color: "#94a3b8", fontSize: 12,
                                       marginTop: 2,
                                       fontFamily: "ui-monospace, monospace" }}>{subtitle}</div>}
        </div>
        {badge && (
          <span style={{
            padding: "3px 12px", borderRadius: 999,
            background: `${badgeColor}22`, border: `1px solid ${badgeColor}66`,
            color: badgeColor, fontSize: 11, fontWeight: 800,
            letterSpacing: 0.5, textTransform: "uppercase",
          }}>{badge}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function KV({ pairs }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {pairs.map(([k, v], i) => (
        <div key={i} style={{
          padding: "10px 14px", background: "#080c12",
          border: "1px solid #1e2530", borderRadius: 8,
        }}>
          <div style={{ color: "#64748b", fontSize: 9, fontWeight: 700,
                          letterSpacing: 0.7, textTransform: "uppercase" }}>{k}</div>
          <div style={{ color: "#f1f5f9", fontSize: 13, marginTop: 3,
                          fontFamily: "ui-monospace, monospace",
                          wordBreak: "break-word" }}>
            {v || "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

const partnerBoxStyle = {
  padding: 12, background: "#080c12", border: "1px solid #1e2530", borderRadius: 8,
};
